/**
 * Step 10 — the text on every video (round-2 §2.4, step id `signoff`).
 *
 * Every line burned into the brand's videos is a ROW here: the reel's own
 * lines (the study's overlays — a handle, «Ecris "BRAND" en commentaire»), the
 * ones the operator adds, and nothing hidden behind "…and N more we applied".
 * Each row is a text input with a remove button; "Add a line" appends one.
 * Rows keep their own timing and position — editing a row changes ONLY its
 * text, so a reel line that plays out the last ten seconds still does.
 *
 * What is typed becomes the SAME `DEFAULT_OVERLAY` the study and the Studio
 * produce (top-left, 56 px, the caption face), so Adjust's Extra-text tab
 * opens on an overlay it already knows how to edit. The one deliberate
 * difference is TIMING for a line that lands at index 0: `DEFAULT_OVERLAY`
 * starts nine seconds before the end, off-screen on a six-second sample, so
 * that slot takes the preview timing (`SIGNOFF_PREVIEW_*`) and
 * `buildLayoutPayload` puts the shipped timing back — exactly today's rule.
 * (The phone shows every row regardless: `SamplePhone`'s `overlaysAlwaysOn`
 * pins each overlay to the whole loop for the PREVIEW only.)
 *
 * An EMPTY row is UI-only. Nothing is written until it has text, and a row
 * emptied by the operator drops its line on the next commit — "no text, no
 * line", the same answer the single field gave. Every write REPLACES the
 * overlay list (`layoutPatch` with `overlays` has replace semantics), and a
 * step that is only read dispatches nothing at all (spec §2.3).
 */
import { useEffect, useMemo, useRef, useState, type Dispatch } from "react";
import { Plus, Trash2 } from "lucide-react";
import type { RenderTextOverlay } from "../../../../../src/remotion/types.ts";
import { effectiveDesign, type BrandSetupAction, type BrandSetupState } from "../../../lib/brandSetup/state";
import { overlaysFromRows, type TextLineRow } from "../../../lib/brandSetup/textLines";
import { MONO_LABEL, ReelTag } from "../ui";

const OVERLAY_FIELD = "layout.overlays";
export const SIGNOFF_MAX = 140;
/** A bound on "Add a line" — nothing downstream caps the count, and a video
 *  with more burned-in lines than this is not a brand style, it is noise. */
export const SIGNOFF_MAX_LINES = 6;
/** Long enough that a phrase is not re-dispatched letter by letter, short
 *  enough that the phone follows the typing rather than reporting it. */
const DEBOUNCE_MS = 150;

/** A `TextLineRow` with a stable React key — rows are removed by identity,
 *  never by index. The rows → overlays rules live in `lib/brandSetup/textLines`. */
interface Row extends TextLineRow {
  key: number;
}

let keySeq = 0;
const nextKey = (): number => ++keySeq;

const serialize = (list: readonly RenderTextOverlay[]): string => JSON.stringify(list);

export function SignoffStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
}): React.JSX.Element {
  const { state, dispatch } = props;
  const overlays = effectiveDesign(state)?.layout?.overlays ?? [];

  // Seeded ONCE from what the wizard holds. No overlays → one empty input,
  // so the screen is never a bare "Add a line" button.
  const [rows, setRows] = useState<Row[]>(() =>
    overlays.length
      ? overlays.map((o) => ({ key: nextKey(), text: o.text, base: o }))
      : [{ key: nextKey(), text: "", base: null }],
  );
  /** The row "Add a line" just made, so its input takes focus. */
  const [focusKey, setFocusKey] = useState<number | null>(null);

  const pending = useMemo(() => overlaysFromRows(rows), [rows]);

  // Refs, not deps: the commit has to survive the step unmounting (Continue
  // clicked 40 ms after the last keystroke) without re-running on every
  // render of the wizard around it. `committedRef` is seeded from the SAME
  // function the rows go through, so a visit that only reads commits nothing.
  const committedRef = useRef<string>(serialize(overlaysFromRows(rows)));
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const commitRef = useRef<(list: RenderTextOverlay[]) => void>(() => {});
  commitRef.current = (list: RenderTextOverlay[]): void => {
    const next = serialize(list);
    if (next === committedRef.current) return;
    committedRef.current = next;
    dispatch({
      type: "layoutPatch",
      patch: { overlays: list },
      label: list.length ? "Text on the video" : "No text on the video",
    });
  };

  useEffect(() => {
    if (serialize(pending) === committedRef.current) return;
    const timer = window.setTimeout(() => commitRef.current(pending), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [pending]);

  // Leaving the step mid-debounce must not lose a line. The wizard's reducer
  // outlives this component, so the flush is safe on the way out.
  useEffect(() => () => commitRef.current(pendingRef.current), []);

  /**
   * Which texts came off the reel. The study record's own overlays are the
   * source of truth; after a refresh the record is gone from state (the draft
   * strips it) and the accepted chip's " · "-joined value stands in. A row is
   * "from your reel" while its text is still one of those, verbatim — edit
   * it and it is the operator's.
   */
  const reelTexts = useMemo(() => {
    const fromRecord = state.study?.patch?.layout?.overlays?.map((o) => o.text.trim()).filter(Boolean) ?? [];
    if (fromRecord.length) return new Set(fromRecord);
    const chip = state.chips.find((c) => c.field === OVERLAY_FIELD);
    const value = typeof chip?.value === "string" ? chip.value : "";
    return new Set(value.split(" · ").map((t) => t.trim()).filter(Boolean));
  }, [state.study, state.chips]);

  const setText = (key: number, text: string): void =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, text } : r)));

  const remove = (key: number): void =>
    setRows((prev) => {
      const rest = prev.filter((r) => r.key !== key);
      // Never an empty screen: the last line gone leaves one empty input.
      return rest.length ? rest : [{ key: nextKey(), text: "", base: null }];
    });

  const add = (): void => {
    const key = nextKey();
    setRows((prev) => (prev.length >= SIGNOFF_MAX_LINES ? prev : [...prev, { key, text: "", base: null }]));
    setFocusKey(key);
  };

  const full = rows.length >= SIGNOFF_MAX_LINES;

  return (
    <div style={{ display: "grid", gap: 14, maxWidth: 520 }}>
      <div style={MONO_LABEL}>Lines on the video</div>

      <div style={{ display: "grid", gap: 12 }}>
        {rows.map((row, i) => {
          const fromReel = reelTexts.has(row.text.trim());
          return (
            <div key={row.key} style={{ display: "grid", gap: 6 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {/* A TEXTAREA, not an input: an overlay may carry a line
                    break (the reel's CTA does — «…comme le nôtre ?⏎Ecris…»)
                    and an <input> silently strips it, gluing the two lines
                    together on the first edit. StepFrame leaves plain Enter to
                    a textarea (a new line) and takes ⌘/Ctrl+Enter as Continue. */}
                <textarea
                  data-autofocus={i === 0 ? true : undefined}
                  autoFocus={row.key === focusKey}
                  value={row.text}
                  rows={Math.min(3, Math.max(1, row.text.split("\n").length))}
                  maxLength={SIGNOFF_MAX}
                  spellCheck={false}
                  aria-label={`Line ${i + 1}`}
                  placeholder={i === 0 ? "e.g. @courtetclair · Follow for more" : "Another line"}
                  onChange={(e) => setText(row.key, e.target.value)}
                  style={INPUT}
                />
                <button
                  type="button"
                  onClick={() => remove(row.key)}
                  aria-label={`Remove line ${i + 1}`}
                  title="Remove this line"
                  style={ICON_BUTTON}
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 18 }}>
                <span style={{ flex: 1, minWidth: 0 }}>{fromReel && <ReelTag label="From your reel" />}</span>
                <span style={{ fontSize: 11.5, color: "var(--ql-fg-faint)", fontFamily: "var(--ql-font-mono)" }}>
                  {row.text.length}/{SIGNOFF_MAX}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={add}
          disabled={full}
          title={full ? `Up to ${SIGNOFF_MAX_LINES} lines` : "Add another line"}
          style={{ ...GHOST_BUTTON, opacity: full ? 0.5 : 1, cursor: full ? "default" : "pointer" }}
        >
          <Plus size={14} />
          Add a line
        </button>
        <span style={{ fontSize: 12.5, color: "var(--ql-fg-subtle)", flex: 1, minWidth: 0 }}>
          Enter breaks a line in two; ⌘↵ or Ctrl↵ continues. Leave a line empty to drop it — nothing here means no text on the video.
        </span>
      </div>
    </div>
  );
}

const INPUT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "block",
  padding: "13px 15px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(17,24,39,.5)",
  color: "var(--ql-fg)",
  fontSize: 16,
  lineHeight: 1.4,
  fontFamily: "inherit",
  outline: "none",
  resize: "none",
  boxSizing: "border-box",
};

const ICON_BUTTON: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.10)",
  background: "transparent",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
};

const GHOST_BUTTON: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.12)",
  background: "transparent",
  color: "var(--ql-fg)",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "inherit",
};
